import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import type {
    Next7DaysEventItem,
    Next7DaysEventsPayload,
} from '../../../../../../server/controllers/portal/dashboard-card-payloads.js'
import { Award, Cake, LogOut, Sparkles } from '../../../../../components/icon/solar-icons.js'
import { PortalAvatar } from '../../../../../components/portal-avatar/portal-avatar.js'
import './event-list.css'

const dayLabel = (lang: Language, daysUntil: number): string => {
    if (daysUntil === 0) return t.portal.dashboard.cards.next_7_days_events.today(lang)
    if (daysUntil === 1) return t.portal.dashboard.cards.next_7_days_events.tomorrow(lang)
    return t.portal.dashboard.cards.next_7_days_events.inDays(daysUntil)(lang)
}

const typeIcon = (type: Next7DaysEventItem['type']) => {
    switch (type) {
        case 'birthday':
            return <Cake size={14} strokeWidth={2} aria-hidden="true" />
        case 'anniversary':
            return <Award size={14} strokeWidth={2} aria-hidden="true" />
        case 'offboarding':
            return <LogOut size={14} strokeWidth={2} aria-hidden="true" />
        default:
            return <Sparkles size={14} strokeWidth={2} aria-hidden="true" />
    }
}

const typeLabel = (event: Next7DaysEventItem, lang: Language): string => {
    switch (event.type) {
        case 'birthday':
            return t.portal.dashboard.cards.next_7_days_events.typeBirthday(lang)
        case 'anniversary':
            return event.years && event.years > 0
                ? t.portal.dashboard.cards.next_7_days_events.typeAnniversaryYears(event.years)(
                      lang,
                  )
                : t.portal.dashboard.cards.next_7_days_events.typeAnniversary(lang)
        case 'offboarding':
            return t.portal.dashboard.cards.next_7_days_events.typeOffboarding(lang)
        default:
            return t.portal.dashboard.cards.next_7_days_events.typeNewJoiner(lang)
    }
}

// Card-local "DD. month" formatter (e.g. "12. juuni" / "12 June"). Distinct
// from the shared formatDisplayDate('long') because we deliberately omit the
// year — the card only shows upcoming events so the year is implied.
const formatDayMonth = (isoDate: string, lang: Language): string => {
    const date = new Date(isoDate + 'T00:00:00')
    const locale = lang === 'et' ? 'et-EE' : 'en-GB'
    const day = date.getDate()
    const month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(date)
    return lang === 'et' ? `${day}. ${month}` : `${day} ${month}`
}

export const Next7DaysEventsCard = ({
    data,
    lang,
}: {
    data: Next7DaysEventsPayload
    lang: Language
}) => {
    if (data.events.length === 0) {
        return (
            <div className="portal-dashboard__absence-empty">
                {t.portal.dashboard.cards.next_7_days_events.empty(lang)}
            </div>
        )
    }

    return (
        <div className="portal-event-list__list">
            {data.events.map((e) => (
                <div key={`${e.type}-${e.employeeId}-${e.date}`} className="portal-event-list__row">
                    <PortalAvatar
                        name={`${e.firstName} ${e.lastName}`}
                        avatarUrl={e.avatarUrl}
                        px={30}
                    />
                    <div className="portal-event-list__person">
                        <div className="portal-event-list__name">
                            {e.firstName} {e.lastName}
                        </div>
                    </div>
                    <span
                        className={`portal-event-list__type portal-event-list__type--${e.type}`}
                        title={formatDayMonth(e.date, lang)}
                    >
                        {typeIcon(e.type)}
                        <span className="portal-event-list__type-text">
                            {typeLabel(e, lang)} · {dayLabel(lang, e.daysUntil)}
                        </span>
                    </span>
                </div>
            ))}
        </div>
    )
}
